'use client';

import { SDKResponse } from '@commercetools/frontend-sdk';
import { ProjectSettings } from 'shared/types/ProjectSettings';

export interface GAEventPayload {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

const analytics = {
  gaTrackingId: undefined as string | undefined,
  isInitializedMap: {} as Record<string, boolean>,
  lastInitialData: undefined as Record<string, any> | undefined,
  lastPageName: undefined as string | undefined,
  // Defaults to true so trackEvent() behaves normally when GASnippet isn't involved (e.g. isolated tests).
  dataLayerReady: true as boolean,
  eventQueue: [] as { eventName: string; payload: GAEventPayload }[],
  // Promotion IDs already reported via view_promotion for the current page view.
  viewedPromotionIds: new Set<string>(),
  setGaTrackingId: (id: string | undefined) => {
    analytics.gaTrackingId = id;
  },
  previewModeParams: undefined as string | undefined,
  setGaPreviewModeParams: (previewMode: string | undefined) => {
    analytics.previewModeParams = previewMode;
  },

  // Called on every page navigation so events queue behind the upcoming data_layer_ready push.
  markPageChange: (pageName?: string) => {
    if (analytics.lastPageName === pageName) return;
    analytics.lastPageName = pageName;
    analytics.dataLayerReady = false;
    analytics.eventQueue = [];
    analytics.viewedPromotionIds.clear();
  },

  hasViewedPromotion: (promotionId: string) => analytics.viewedPromotionIds.has(promotionId),
  markPromotionViewed: (promotionId: string) => {
    analytics.viewedPromotionIds.add(promotionId);
  },

  flushEventQueue: () => {
    if (typeof window === 'undefined' || !analytics.eventQueue.length) return;
    window.dataLayer = window.dataLayer || [];
    analytics.eventQueue.forEach(({ eventName, payload }) => {
      window.dataLayer.push({ event: eventName, ...payload });
    });
    analytics.eventQueue = [];
  },

  initialize: (analyticsInitialData: Record<string, any>, shouldRetrigger: boolean) => {
    const gaTrackingId = analytics.gaTrackingId;
    if (
      !gaTrackingId ||
      typeof window === 'undefined' ||
      (analytics.isInitializedMap[gaTrackingId] && !shouldRetrigger)
    )
      return;
    analytics.isInitializedMap[gaTrackingId] = true;

    if (typeof document === 'undefined') return;

    // 1. Push initial data to dataLayer (before GTM script)
    window.dataLayer = window.dataLayer || [];
    if (analyticsInitialData && Object.keys(analyticsInitialData).length > 0) {
      window.dataLayer.push(analyticsInitialData);
    }

    // data_layer_ready has now fired for this page, so queued events can safely follow it.
    analytics.dataLayerReady = true;
    analytics.flushEventQueue();

    // 2. Inject the standard GTM container script (only once)
    // Inject GTM script if not already present
    if (!document.querySelector(`script[src*="gtm.js"][src*="${gaTrackingId}"]`)) {
      const gtmScript = document.createElement('script');
      gtmScript.dataset.gtmInitializer = 'true';
      gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl${analytics.previewModeParams ? "+'" + analytics.previewModeParams + "'" : ''};f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gaTrackingId}');`;
      document.head.appendChild(gtmScript);
    }

    analytics.lastInitialData = analyticsInitialData;
  },

  trackEvent: (eventName: string, payload: GAEventPayload) => {
    const gaTrackingId = analytics.gaTrackingId;
    if (!gaTrackingId || typeof window === 'undefined') return;

    // Hold events until data_layer_ready has been pushed so it always fires first.
    if (!analytics.dataLayerReady) {
      analytics.eventQueue.push({ eventName, payload });
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload,
    });
  },
};

export function setFieldsFromProjectSettings(gaTrackingId?: string, previewModeParams?: string) {
  analytics.setGaTrackingId(gaTrackingId);
  analytics.setGaPreviewModeParams(previewModeParams);
}

export function useAnalytics() {
  return analytics;
}

export const trackEvent = (eventName: string, payload: GAEventPayload): void => {
  analytics.trackEvent(eventName, payload);
};

export const hasViewedPromotion = (promotionId: string): boolean => analytics.hasViewedPromotion(promotionId);

export const markPromotionViewed = (promotionId: string): void => analytics.markPromotionViewed(promotionId);

export const GABackupScript = ({ projectSettings }: { projectSettings: SDKResponse<ProjectSettings> }) => {
  const { gaTrackingId, previewModeParams } = !projectSettings.isError ? projectSettings.data : {};

  if (!gaTrackingId) return null;

  return (
    <noscript>
      <iframe
        title="GTM Backup"
        src={`https://www.googletagmanager.com/ns.html?id=${gaTrackingId}${previewModeParams}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      ></iframe>
    </noscript>
  );
};
